import React, { useState, useMemo } from 'react';
import { useNotification } from '../../../../context/NotificationContext';
import DocumentGallery from './DocumentGallery';
import styles from './ApplicationPipeline.module.css';

/**
 * ApplicationPipeline - Phase 3C
 * 
 * Application review component with:
 * - Status filtering
 * - Application cards with details
 * - Approve/Reject/Delete actions
 * - Detail modal with form data and documents
 */
const ApplicationPipeline = ({ 
  programId,
  applications = [],
  templates = {},
  loading = false,
  onRefresh,
  onApprove,
  onReject,
  onDelete
}) => {
  const { showSuccess, showError } = useNotification();
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);

  // Status configuration
  const STATUS_CONFIG = {
    draft: { label: 'Draft', color: '#6b7280', icon: '📝', bgColor: 'rgba(107, 114, 128, 0.15)' },
    submitted: { label: 'Submitted', color: '#3b82f6', icon: '📋', bgColor: 'rgba(59, 130, 246, 0.15)' },
    under_review: { label: 'Under Review', color: '#f59e0b', icon: '👀', bgColor: 'rgba(245, 158, 11, 0.15)' },
    documents_pending: { label: 'Documents Pending', color: '#eab308', icon: '📎', bgColor: 'rgba(234, 179, 8, 0.15)' },
    payment_pending: { label: 'Payment Pending', color: '#f97316', icon: '💳', bgColor: 'rgba(249, 115, 22, 0.15)' },
    approved: { label: 'Approved', color: '#22c55e', icon: '✅', bgColor: 'rgba(34, 197, 94, 0.15)' },
    rejected: { label: 'Rejected', color: '#ef4444', icon: '❌', bgColor: 'rgba(239, 68, 68, 0.15)' },
    enrolled: { label: 'Enrolled', color: '#8b5cf6', icon: '🎓', bgColor: 'rgba(139, 92, 246, 0.15)' },
    waitlisted: { label: 'Waitlisted', color: '#f59e0b', icon: '⏳', bgColor: 'rgba(245, 158, 11, 0.15)' },
    cancelled: { label: 'Cancelled', color: '#6b7280', icon: '🚫', bgColor: 'rgba(107, 114, 128, 0.15)' },
    expired: { label: 'Expired', color: '#9ca3af', icon: '⌛', bgColor: 'rgba(156, 163, 175, 0.15)' },
    superseded: { label: 'Superseded', color: '#9ca3af', icon: '🔄', bgColor: 'rgba(156, 163, 175, 0.15)' }
  };

  const statusOptions = [
    { value: 'all', label: 'All Applications' },
    { value: 'pending', label: 'Pending Review', filter: ['submitted', 'under_review', 'documents_pending'] },
    { value: 'draft', label: 'Draft' },
    { value: 'submitted', label: 'Submitted' },
    { value: 'under_review', label: 'Under Review' },
    { value: 'documents_pending', label: 'Documents Pending' },
    { value: 'waitlisted', label: 'Waitlisted' },
    { value: 'approved', label: 'Approved' },
    { value: 'rejected', label: 'Rejected' }
  ];

  const normalizeStatus = (status) => status?.toLowerCase() || 'submitted';

  // Filter applications
  const filteredApplications = useMemo(() => {
    if (selectedStatus === 'all') return applications;
    
    const option = statusOptions.find(o => o.value === selectedStatus);
    if (option?.filter) {
      return applications.filter(app => option.filter.includes(normalizeStatus(app.status)));
    }
    return applications.filter(app => normalizeStatus(app.status) === selectedStatus);
  }, [applications, selectedStatus]);

  // Get counts
  const counts = useMemo(() => {
    const result = { all: applications.length, pending: 0 };
    applications.forEach(app => {
      const s = normalizeStatus(app.status);
      result[s] = (result[s] || 0) + 1;
      if (['submitted', 'under_review', 'documents_pending'].includes(s)) {
        result.pending++;
      }
    });
    return result;
  }, [applications]);

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Invalid date';
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const parseFormData = (raw) => {
    if (!raw) return {};
    if (typeof raw === 'string') {
      try { return JSON.parse(raw); } catch { return {}; }
    }
    return raw;
  };

  const getApplicantName = (app) => {
    const fd = parseFormData(app.formData);
    const firstName = fd?.personalInfo?.firstName || '';
    const lastName = fd?.personalInfo?.lastName || '';
    return `${firstName} ${lastName}`.trim() || 'Unknown Applicant';
  };

  // Handle approve
  const handleApprove = async (applicationId) => {
    setActionLoading(applicationId);
    try {
      await onApprove?.(applicationId);
      await onRefresh?.();
      showSuccess('Application approved successfully');
      setSelectedApplication(null);
    } catch (err) {
      showError(err.message || 'Failed to approve application');
    } finally {
      setActionLoading(null);
    }
  };

  // Handle reject
  const handleReject = async (applicationId) => {
    const reason = window.prompt('Please provide a reason for rejection:');
    if (!reason) return;

    setActionLoading(applicationId);
    try {
      await onReject?.(applicationId, reason);
      await onRefresh?.();
      showSuccess('Application rejected');
      setSelectedApplication(null);
    } catch (err) {
      showError(err.message || 'Failed to reject application');
    } finally {
      setActionLoading(null);
    }
  };

  // Handle delete
  const handleDelete = async (applicationId, appNumber) => {
    if (!window.confirm(`Delete application ${appNumber}? This cannot be undone.`)) return;

    setActionLoading(applicationId);
    try {
      await onDelete?.(applicationId);
      showSuccess('Application deleted');
      setSelectedApplication(null);
    } catch (err) {
      showError(err.message || 'Failed to delete application');
    } finally {
      setActionLoading(null);
    }
  };

  // Get template for application
  const getTemplate = (app) => {
    const templateId = typeof app.templateId === 'object' ? app.templateId._id : app.templateId;
    return templates[templateId] || null;
  };

  // Map formData section keys back to template section IDs
  const SECTION_KEY_TO_ID = {
    personalInfo: 'personal_info',
    emergencyContact: 'emergency_contact',
    healthInfo: 'health_info',
    parentGuardian: 'parent_guardian',
    soccerExperience: 'soccer_experience',
    medicalClearance: 'medical_clearance'
  };

  const renderFormSection = (sectionKey, sectionData, template) => {
    if (!sectionData || typeof sectionData !== 'object') return null;

    const sectionMappings = {
      personalInfo: 'Personal Information',
      emergencyContact: 'Emergency Contact',
      healthInfo: 'Health Information',
      parentGuardian: 'Parent/Guardian',
      soccerExperience: 'Soccer Experience',
      medicalClearance: 'Medical Clearance'
    };

    const sectionTitle = sectionMappings[sectionKey] || sectionKey.replace(/([A-Z])/g, ' $1').trim();

    const formatValue = (value) => {
      if (value === null || value === undefined || value === '') return 'Not provided';
      if (typeof value === 'boolean') return value ? 'Yes' : 'No';
      if (Array.isArray(value)) return value.length > 0 ? value.join(', ') : 'None';
      return String(value);
    };

    const formatLabel = (key) => {
      return key
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, str => str.toUpperCase())
        .trim();
    };

    // Determine which fields to show based on the template
    const templateSectionId = SECTION_KEY_TO_ID[sectionKey];
    const templateSection = template?.sections?.find(s => s.id === templateSectionId);
    let entries;

    if (templateSection?.fields) {
      // Show only fields defined in the template (in template order), using template labels
      entries = templateSection.fields.map(field => ({
        key: field.id,
        label: field.label,
        value: sectionData[field.id]
      }));
    } else {
      // No template match: show fields that have actual values
      entries = Object.entries(sectionData)
        .filter(([, value]) => value !== null && value !== undefined && value !== '')
        .map(([key, value]) => ({ key, label: formatLabel(key), value }));
    }

    if (entries.length === 0) return null;

    return (
      <div key={sectionKey} className={styles.formSection}>
        <h4>{sectionTitle}</h4>
        <div className={styles.formGrid}>
          {entries.map(({ key, label, value }) => (
            <div key={key} className={styles.formField}>
              <span className={styles.fieldLabel}>{label}</span>
              <span className={styles.fieldValue}>{formatValue(value)}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const canApproveReject = (status) => {
    return ['submitted', 'under_review', 'documents_pending', 'waitlisted'].includes(normalizeStatus(status));
  };

  const canDelete = (status) => {
    return !['approved', 'enrolled'].includes(normalizeStatus(status));
  };

  if (loading) {
    return (
      <div className={styles.loading}>
        <div className={styles.spinner}></div>
        <p>Loading applications...</p>
      </div>
    );
  }

  return (
    <div className={styles.applicationPipeline}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <h2>Applications</h2>
          <p>{applications.length} total · {counts.pending} pending review</p>
        </div>
        <div className={styles.headerRight}>
          <select 
            className={styles.statusFilter}
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
          >
            {statusOptions.map(opt => (
              <option key={opt.value} value={opt.value}>
                {opt.label} {counts[opt.value] !== undefined ? `(${counts[opt.value]})` : ''}
              </option>
            ))}
          </select>
          <button className={styles.refreshBtn} onClick={onRefresh}>
            🔄 Refresh
          </button>
        </div>
      </div>

      {/* Applications Grid */}
      {filteredApplications.length === 0 ? (
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon}>📋</div>
          <h3>No applications found</h3>
          <p>
            {selectedStatus === 'all' 
              ? 'No registration applications have been submitted yet.'
              : `No applications with status "${statusOptions.find(o => o.value === selectedStatus)?.label}"`
            }
          </p>
        </div>
      ) : (
        <div className={styles.applicationGrid}>
          {filteredApplications.map(app => {
            const statusConfig = STATUS_CONFIG[normalizeStatus(app.status)] || { label: normalizeStatus(app.status), color: '#6b7280', icon: '❓', bgColor: 'rgba(107, 114, 128, 0.15)' };
            const isLoading = actionLoading === app._id;
            const fd = parseFormData(app.formData);

            return (
              <div key={app._id} className={styles.applicationCard}>
                <div className={styles.cardHeader}>
                  <div className={styles.applicantInfo}>
                    <h3>{getApplicantName(app)}</h3>
                    <span className={styles.appNumber}>
                      #{app.applicationNumber || app._id.slice(-8)}
                    </span>
                  </div>
                  <div 
                    className={styles.statusBadge}
                    style={{ 
                      backgroundColor: statusConfig.bgColor,
                      color: statusConfig.color 
                    }}
                  >
                    <span>{statusConfig.icon}</span>
                    <span>{statusConfig.label}</span>
                  </div>
                </div>

                <div className={styles.cardBody}>
                  <div className={styles.infoRow}>
                    <span className={styles.infoLabel}>📧 Email</span>
                    <span className={styles.infoValue}>
                      {fd?.personalInfo?.email || 'Not provided'}
                    </span>
                  </div>
                  <div className={styles.infoRow}>
                    <span className={styles.infoLabel}>📱 Phone</span>
                    <span className={styles.infoValue}>
                      {fd?.personalInfo?.phone || 'Not provided'}
                    </span>
                  </div>
                  <div className={styles.infoRow}>
                    <span className={styles.infoLabel}>📅 Submitted</span>
                    <span className={styles.infoValue}>
                      {formatDate(app.submittedAt || app.createdAt)}
                    </span>
                  </div>
                  <div className={styles.infoRow}>
                    <span className={styles.infoLabel}>📊 Completion</span>
                    <div className={styles.progressBar}>
                      <div 
                        className={styles.progressFill}
                        style={{ width: `${app.completionPercentage || 0}%` }}
                      ></div>
                      <span>{app.completionPercentage || 0}%</span>
                    </div>
                  </div>
                </div>

                {/* Documents Status */}
                {app.documents && app.documents.length > 0 && (
                  <div className={styles.documentsPreview}>
                    <span className={styles.docsLabel}>Documents:</span>
                    <div className={styles.docBadges}>
                      {app.documents.map((doc, idx) => {
                        const hasFiles = doc.files && doc.files.length > 0;
                        const isVerified = doc.files?.some(f => f.status === 'verified');
                        return (
                          <span 
                            key={idx}
                            className={`${styles.docBadge} ${isVerified ? styles.verified : hasFiles ? styles.pending : styles.missing}`}
                            title={doc.requirementId}
                          >
                            {doc.requirementId.slice(0, 3).toUpperCase()}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className={styles.cardActions}>
                  <button 
                    className={styles.viewBtn}
                    onClick={() => setSelectedApplication(app)}
                  >
                    👁️ View
                  </button>
                  
                  {canApproveReject(app.status) && (
                    <>
                      <button 
                        className={styles.approveBtn}
                        onClick={() => handleApprove(app._id)}
                        disabled={isLoading}
                      >
                        {isLoading ? '...' : '✅'}
                      </button>
                      <button 
                        className={styles.rejectBtn}
                        onClick={() => handleReject(app._id)}
                        disabled={isLoading}
                      >
                        {isLoading ? '...' : '❌'}
                      </button>
                    </>
                  )}
                  
                  {canDelete(app.status) && (
                    <button 
                      className={styles.deleteBtn}
                      onClick={() => handleDelete(app._id, app.applicationNumber)}
                      disabled={isLoading}
                    >
                      🗑️
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Application Detail Modal */}
      {selectedApplication && (
        <div className={styles.modalOverlay} onClick={() => setSelectedApplication(null)}>
          <div className={styles.modal} onClick={e => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <div>
                <h3>Application Details</h3>
                <p>#{selectedApplication.applicationNumber || selectedApplication._id.slice(-8)}</p>
              </div>
              <button 
                className={styles.closeBtn}
                onClick={() => setSelectedApplication(null)}
              >
                ✕
              </button>
            </div>

            <div className={styles.modalBody}>
              {/* Status Banner */}
              <div 
                className={styles.statusBanner}
                style={{ 
                  backgroundColor: (STATUS_CONFIG[normalizeStatus(selectedApplication.status)] || { bgColor: 'rgba(107, 114, 128, 0.15)' }).bgColor,
                  borderColor: (STATUS_CONFIG[normalizeStatus(selectedApplication.status)] || { color: '#6b7280' }).color
                }}
              >
                <span>{(STATUS_CONFIG[normalizeStatus(selectedApplication.status)] || { icon: '❓' }).icon}</span>
                <span>{(STATUS_CONFIG[normalizeStatus(selectedApplication.status)] || { label: normalizeStatus(selectedApplication.status) }).label}</span>
                <span className={styles.statusDate}>
                  Last updated: {formatDate(selectedApplication.updatedAt)}
                </span>
              </div>

              {/* Form Data */}
              <div className={styles.formSections}>
                {selectedApplication.formData && 
                  Object.entries(parseFormData(selectedApplication.formData)).map(([key, value]) => 
                    renderFormSection(key, value, getTemplate(selectedApplication))
                  )
                }
              </div>

              {/* Documents */}
              <div className={styles.documentsSection}>
                <h4>📎 Uploaded Documents</h4>
                <DocumentGallery 
                  application={selectedApplication}
                  showMetadata={true}
                />
              </div>
            </div>

            <div className={styles.modalFooter}>
              {canApproveReject(selectedApplication.status) && (
                <>
                  <button 
                    className={styles.approveFullBtn}
                    onClick={() => handleApprove(selectedApplication._id)}
                    disabled={actionLoading === selectedApplication._id}
                  >
                    ✅ Approve Application
                  </button>
                  <button 
                    className={styles.rejectFullBtn}
                    onClick={() => handleReject(selectedApplication._id)}
                    disabled={actionLoading === selectedApplication._id}
                  >
                    ❌ Reject Application
                  </button>
                </>
              )}
              <button 
                className={styles.closeFullBtn}
                onClick={() => setSelectedApplication(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApplicationPipeline;
